import React, { useState, useEffect } from 'react';
import { Star, Eye, EyeOff, Trash2, MessageSquare, Filter } from 'lucide-react';
import { reviewsApi, AdminReview } from '@/services/reviewsApi';
import toast from 'react-hot-toast';

export const ReviewsList: React.FC = () => {
    const [reviews, setReviews] = useState<AdminReview[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<string>('');

    useEffect(() => {
        loadReviews();
    }, [statusFilter]);

    const loadReviews = async () => {
        try {
            setLoading(true);
            const params: any = {};
            if (statusFilter) params.status = statusFilter;
            const data = await reviewsApi.getReviews(params);
            setReviews(data);
        } catch (error: any) {
            toast.error(error.message || 'Yorumlar yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (review: AdminReview) => {
        const newStatus = review.status === 'approved' ? 'inactive' : 'approved';
        try {
            await reviewsApi.updateStatus(review.id, newStatus);
            setReviews((prev) =>
                prev.map((r) => (r.id === review.id ? { ...r, status: newStatus } : r))
            );
            toast.success(newStatus === 'approved' ? 'Yorum aktifleştirildi' : 'Yorum pasife alındı');
        } catch (error: any) {
            toast.error(error.message || 'İşlem başarısız');
        }
    };

    const handleDelete = async (reviewId: string) => {
        if (!window.confirm('Bu yorumu kalıcı olarak silmek istediğinize emin misiniz?')) return;
        try {
            await reviewsApi.deleteReview(reviewId);
            setReviews((prev) => prev.filter((r) => r.id !== reviewId));
            toast.success('Yorum silindi');
        } catch (error: any) {
            toast.error(error.message || 'Silme başarısız');
        }
    };

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('tr-TR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <MessageSquare size={28} className="text-blue-500" />
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Ürün Yorumları</h1>
                        <p className="text-sm text-gray-500">{reviews.length} yorum</p>
                    </div>
                </div>

                {/* Filter */}
                <div className="flex items-center gap-2">
                    <Filter size={16} className="text-gray-400" />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Tümü</option>
                        <option value="approved">Aktif</option>
                        <option value="inactive">Pasif</option>
                    </select>
                </div>
            </div>

            {/* Table */}
            {loading ? (
                <div className="flex justify-center py-16">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
            ) : reviews.length === 0 ? (
                <div className="text-center py-16 text-gray-500">
                    <MessageSquare size={48} className="mx-auto mb-4 opacity-30" />
                    <p>Henüz yorum yok</p>
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Ürün</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Kullanıcı</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Puan</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Yorum</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Durum</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Tarih</th>
                                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase">İşlem</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {reviews.map((review) => (
                                <tr key={review.id} className={`hover:bg-gray-50 ${review.status === 'inactive' ? 'opacity-60' : ''}`}>
                                    <td className="px-4 py-3">
                                        <span className="text-sm font-medium text-gray-900 line-clamp-1 max-w-[200px] block">
                                            {review.productTitle}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="text-sm text-gray-700">{review.userName}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-0.5">
                                            {[...Array(5)].map((_, i) => (
                                                <Star
                                                    key={i}
                                                    size={14}
                                                    className={i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
                                                />
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <p className="text-sm text-gray-700 line-clamp-2 max-w-[300px]">{review.comment}</p>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span
                                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                review.status === 'approved'
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-red-100 text-red-700'
                                            }`}
                                        >
                                            {review.status === 'approved' ? 'Aktif' : 'Pasif'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="text-xs text-gray-500">{formatDate(review.createdAt)}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-end gap-1">
                                            <button
                                                onClick={() => handleToggleStatus(review)}
                                                className={`p-1.5 rounded-lg transition ${
                                                    review.status === 'approved'
                                                        ? 'text-orange-500 hover:bg-orange-50'
                                                        : 'text-green-500 hover:bg-green-50'
                                                }`}
                                                title={review.status === 'approved' ? 'Pasife Al' : 'Aktifleştir'}
                                            >
                                                {review.status === 'approved' ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </button>
                                            <button
                                                onClick={() => handleDelete(review.id)}
                                                className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition"
                                                title="Sil"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
